import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  ArrowLeft, Mail, Send, Loader2, CheckCircle2, XCircle, 
  FileText, Building, RefreshCw, ChevronDown, ChevronRight,
  Clock, Trash2, AlertCircle, X, Pencil, Settings, Image as ImageIcon, Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Minimal supercomputer/data center background
const BG_IMAGE = "https://images.unsplash.com/photo-1700330349498-18fc1dfac02a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHw0fHxkYXRhJTIwY2VudGVyJTIwc2VydmVyJTIwcmFjayUyMGJsdWUlMjBsaWdodHMlMjBmdXR1cmlzdGljfGVufDB8fHx8MTc3MTM2NjQyMnww&ixlib=rb-4.1.0&q=85";

// Typewriter effect component
const TypewriterText = ({ text, speed = 30, className = '' }) => {
  const [displayText, setDisplayText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  
  useEffect(() => {
    setDisplayText('');
    setIsComplete(false);
    let index = 0;
    
    const interval = setInterval(() => {
      if (index < text.length) {
        setDisplayText(text.slice(0, index + 1));
        index++;
      } else {
        setIsComplete(true);
        clearInterval(interval);
      }
    }, speed);
    
    return () => clearInterval(interval);
  }, [text, speed]);
  
  return (
    <span className={className}>
      {displayText}
      {!isComplete && <span className="animate-pulse">|</span>}
    </span>
  );
};

// ==================== NOTIFICATION REPORT PANEL ====================
const NotificationReportPanel = ({ user }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [expandedDays, setExpandedDays] = useState({});
  const [previewEmail, setPreviewEmail] = useState(null);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications/report?user_id=${user.user_id}&days=10`);
      setReport(res.data);
      // Auto-expand today
      if (res.data.days?.length > 0) {
        setExpandedDays({ [res.data.days[0].date]: true });
      }
    } catch {
      toast.error('Σφάλμα φόρτωσης αναφοράς');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSend = async () => {
    if (!report?.total_pending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/notifications/report/send-pending?user_id=${user.user_id}`);
      toast.success(res.data.message);
      setTimeout(fetchReport, 2000);
    } catch {
      toast.error('Σφάλμα αποστολής');
    } finally {
      setSending(false);
    }
  };

  const toggleDay = (date) => {
    setExpandedDays(prev => ({ ...prev, [date]: !prev[date] }));
  };

  const formatDate = (dateStr) => {
    try {
      const [y, m, d] = dateStr.split('-');
      return `${d}/${m}/${y}`;
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Header */}
      <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-3">
            <Clock className="h-6 w-6 text-amber-400" />
            Αναφορές Αποστολών
          </h2>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchReport}
              className="text-white/70 hover:text-white"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            {report?.total_pending > 0 && (
              <Button
                onClick={handleManualSend}
                disabled={sending}
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="manual-send-btn"
              >
                {sending ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Αποστολή...</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" /> Στείλε τώρα ({report.total_pending})</>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-emerald-500/10 rounded-lg p-4 text-center border border-emerald-500/20">
            <div className="text-2xl font-bold text-emerald-400">
              {report?.days?.reduce((sum, d) => sum + d.sent_count, 0) || 0}
            </div>
            <div className="text-xs text-emerald-300/70 mt-1">Παραδόθηκαν (10 ημέρες)</div>
          </div>
          <div className="bg-amber-500/10 rounded-lg p-4 text-center border border-amber-500/20">
            <div className="text-2xl font-bold text-amber-400">
              {report?.total_pending || 0}
            </div>
            <div className="text-xs text-amber-300/70 mt-1">Στην Ουρά</div>
          </div>
          <div className="bg-red-500/10 rounded-lg p-4 text-center border border-red-500/20">
            <div className="text-2xl font-bold text-red-400">
              {report?.days?.reduce((sum, d) => sum + d.failed_count, 0) || 0}
            </div>
            <div className="text-xs text-red-300/70 mt-1">Απέτυχαν (10 ημέρες)</div>
          </div>
        </div>
      </div>

      {/* Daily Breakdown */}
      <div className="space-y-3">
        {report?.days?.length === 0 && (
          <div className="text-center py-12 text-white/50">
            <Mail className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>Δεν υπάρχουν αποστολές τις τελευταίες 10 ημέρες</p>
          </div>
        )}
        {report?.days?.map((day) => (
          <div key={day.date} className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden">
            {/* Day Header (clickable) */}
            <button
              onClick={() => toggleDay(day.date)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/5 transition-colors"
              data-testid={`day-${day.date}`}
            >
              <div className="flex items-center gap-3">
                {expandedDays[day.date]
                  ? <ChevronDown className="h-4 w-4 text-white/50" />
                  : <ChevronRight className="h-4 w-4 text-white/50" />
                }
                <span className="text-white font-medium">{formatDate(day.date)}</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                {day.sent_count > 0 && (
                  <span className="flex items-center gap-1.5 text-emerald-400">
                    <CheckCircle2 className="h-4 w-4" /> {day.sent_count}
                  </span>
                )}
                {day.pending_count > 0 && (
                  <span className="flex items-center gap-1.5 text-amber-400">
                    <Clock className="h-4 w-4" /> {day.pending_count}
                  </span>
                )}
                {day.failed_count > 0 && (
                  <span className="flex items-center gap-1.5 text-red-400">
                    <XCircle className="h-4 w-4" /> {day.failed_count}
                  </span>
                )}
              </div>
            </button>

            {/* Day Detail (expanded) */}
            {expandedDays[day.date] && (
              <div className="border-t border-white/5 px-5 py-3 space-y-1.5 max-h-80 overflow-y-auto">
                {/* Sent */}
                {day.sent.map((email, idx) => (
                  <button
                    key={`s-${idx}`}
                    onClick={() => setPreviewEmail(email)}
                    className="w-full flex items-center gap-3 py-1.5 px-3 rounded bg-emerald-500/5 border border-emerald-500/10 hover:bg-emerald-500/10 transition-colors text-left"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    <span className="text-xs text-white/50 w-12">{email.sent_time || email.scheduled_time}</span>
                    <span className="text-sm text-white/80 truncate flex-1">{email.recipient}</span>
                    <span className="text-xs text-white/40 truncate max-w-[200px]">{email.subject}</span>
                    <Mail className="h-3 w-3 text-white/20 shrink-0" />
                  </button>
                ))}
                {/* Pending */}
                {day.pending.map((email, idx) => (
                  <button
                    key={`p-${idx}`}
                    onClick={() => setPreviewEmail(email)}
                    className="w-full flex items-center gap-3 py-1.5 px-3 rounded bg-amber-500/5 border border-amber-500/10 hover:bg-amber-500/10 transition-colors text-left"
                  >
                    <Clock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                    <span className="text-xs text-white/50 w-12">{email.scheduled_time || '—'}</span>
                    <span className="text-sm text-white/80 truncate flex-1">{email.recipient}</span>
                    <span className="text-xs text-white/40 truncate max-w-[200px]">{email.subject}</span>
                    <Mail className="h-3 w-3 text-white/20 shrink-0" />
                  </button>
                ))}
                {/* Failed */}
                {day.failed.map((email, idx) => (
                  <button
                    key={`f-${idx}`}
                    onClick={() => setPreviewEmail(email)}
                    className="w-full flex items-center gap-3 py-1.5 px-3 rounded bg-red-500/5 border border-red-500/10 hover:bg-red-500/10 transition-colors text-left"
                  >
                    <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                    <span className="text-xs text-white/50 w-12">{email.scheduled_time || '—'}</span>
                    <span className="text-sm text-white/80 truncate flex-1">{email.recipient}</span>
                    <span className="text-xs text-red-400/70 truncate max-w-[200px]">{email.error || email.subject}</span>
                    <Mail className="h-3 w-3 text-white/20 shrink-0" />
                  </button>
                ))}
                {day.sent.length === 0 && day.pending.length === 0 && day.failed.length === 0 && (
                  <div className="text-center py-4 text-white/30 text-sm">Κανένα email</div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Email Preview Modal */}
      {previewEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setPreviewEmail(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50">
              <div>
                <div className="font-medium text-slate-900 text-sm">{previewEmail.subject}</div>
                <div className="text-xs text-slate-500">Προς: {previewEmail.recipient}</div>
              </div>
              <button onClick={() => setPreviewEmail(null)} className="p-1 hover:bg-slate-200 rounded">
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[70vh] p-5">
              {previewEmail.html_content ? (
                <div dangerouslySetInnerHTML={{ __html: previewEmail.html_content }} />
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <Mail className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>Δεν υπάρχει διαθέσιμο preview</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const NotificationAssistant = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('applications');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [selectedEmails, setSelectedEmails] = useState({});
  const [expandedPreviews, setExpandedPreviews] = useState({});
  const [batchStatus, setBatchStatus] = useState(null);
  const [generatingStep, setGeneratingStep] = useState(0);
  const [pendingEmails, setPendingEmails] = useState([]);
  
  // Template filter states
  const [appTemplates, setAppTemplates] = useState([]);
  const [implTemplates, setImplTemplates] = useState([]);
  const [selectedAppTemplate, setSelectedAppTemplate] = useState('all');
  const [selectedImplTemplate, setSelectedImplTemplate] = useState('all');
  
  // Edit email states
  const [editingEmailId, setEditingEmailId] = useState(null);
  const [editedHtmls, setEditedHtmls] = useState({});
  
  // Signature states
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signatureText, setSignatureText] = useState('');
  const [signatureHasImage, setSignatureHasImage] = useState(false);
  const [signaturePreviewSrc, setSignaturePreviewSrc] = useState('');
  const [signatureLoaded, setSignatureLoaded] = useState(false);

  const generatingMessages = [
    'Αναλύω τα έργα σας...',
    'Ελέγχω τα εκκρεμή δικαιολογητικά...',
    'Βρίσκω τους συνεργάτες...',
    'Συντάσσω τα emails...',
    'Ετοιμάζω το preview...'
  ];

  // Fetch pending emails on mount and tab change
  useEffect(() => {
    fetchPendingEmails();
    fetchTemplates();
    if (!signatureLoaded) {
      fetchSignature();
    }
  }, [activeTab]);

  const fetchTemplates = async () => {
    try {
      const [appRes, implRes] = await Promise.all([
        axios.get(`${API}/application-templates?user_id=${user.user_id}`),
        axios.get(`${API}/implementation-templates?user_id=${user.user_id}`)
      ]);
      setAppTemplates(appRes.data || []);
      setImplTemplates(implRes.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchSignature = async () => {
    try {
      const res = await axios.get(`${API}/notifications/signature?user_id=${user.user_id}`);
      setSignatureText(res.data.text || '');
      setSignatureHasImage(res.data.has_image || false);
      // Preview: use API serving endpoint (works always)
      if (res.data.has_image) {
        setSignaturePreviewSrc(`${API}/notifications/signature/image/${user.user_id}?t=${Date.now()}`);
      } else {
        setSignaturePreviewSrc('');
      }
      setSignatureLoaded(true);
    } catch (error) {
      console.error('Error fetching signature:', error);
    }
  };

  const saveSignature = async () => {
    try {
      await axios.put(`${API}/notifications/signature?user_id=${user.user_id}`, {
        text: signatureText,
        image_url: signatureImageUrl
      });
      toast.success('Η υπογραφή αποθηκεύτηκε');
      setSignatureDialogOpen(false);
    } catch (error) {
      toast.error('Σφάλμα αποθήκευσης υπογραφής');
    }
  };

  const buildSignatureHtml = () => {
    if (!signatureText && !signatureHasImage) return '';
    // For emails: use absolute production URL
    const emailImgUrl = `${API}/notifications/signature/image/${user.user_id}`;
    let html = '<div style="margin-top:24px; padding-top:16px; border-top:1px solid #e5e7eb;">';
    if (signatureText) {
      html += `<p style="white-space:pre-line; color:#374151; font-size:13px;">${signatureText}</p>`;
    }
    if (signatureHasImage) {
      html += `<img src="${emailImgUrl}" alt="Υπογραφή" style="max-width:300px; max-height:120px; margin-top:8px;" />`;
    }
    html += '</div>';
    return html;
  };

  const openEditEmail = (item) => {
    // Expand the preview and set it to editing mode
    setExpandedPreviews(prev => ({ ...prev, [item.email_id]: true }));
    setEditingEmailId(item.email_id);
  };

  const saveEditEmail = (emailId) => {
    const el = document.getElementById(`email-preview-${emailId}`);
    if (el) {
      setEditedHtmls(prev => ({ ...prev, [emailId]: el.innerHTML }));
    }
    setEditingEmailId(null);
    toast.success('Το email αποθηκεύτηκε');
  };

  const cancelEditEmail = (emailId) => {
    // Revert: if we had previously saved edits, keep them; otherwise restore original
    const el = document.getElementById(`email-preview-${emailId}`);
    if (el) {
      const item = previewData?.preview_items?.find(i => i.email_id === emailId);
      el.innerHTML = editedHtmls[emailId] || item?.html_preview || '';
    }
    setEditingEmailId(null);
  };

  const fetchPendingEmails = async () => {
    try {
      const response = await axios.get(`${API}/email-queue/pending?user_id=${user.user_id}`);
      setPendingEmails(response.data.emails || []);
    } catch (error) {
      console.error('Error fetching pending emails:', error);
      setPendingEmails([]);
    }
  };

  const clearAllPendingEmails = async () => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε όλα τα pending emails;')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/email-queue/clear?user_id=${user.user_id}`);
      setPendingEmails([]);
      toast.success('Τα pending emails διαγράφηκαν');
    } catch (error) {
      console.error('Error clearing pending emails:', error);
      toast.error('Σφάλμα κατά τη διαγραφή');
    }
  };

  const deleteSinglePendingEmail = async (emailId) => {
    try {
      await axios.delete(`${API}/email-queue/${emailId}`);
      setPendingEmails(prev => prev.filter(e => e.email_id !== emailId));
      toast.success('Το email διαγράφηκε');
    } catch (error) {
      console.error('Error deleting email:', error);
      toast.error('Σφάλμα κατά τη διαγραφή');
    }
  };

  const generatePreview = async () => {
    setGenerating(true);
    setPreviewData(null);
    setSelectedEmails({});
    setBatchStatus(null);
    setGeneratingStep(0);
    
    // Step through messages with typewriter timing
    for (let i = 0; i < generatingMessages.length; i++) {
      setGeneratingStep(i);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    try {
      const endpoint = activeTab === 'applications' 
        ? '/notifications/preview/applications'
        : '/notifications/preview/implementations';
      
      // Add template filter parameter
      const templateFilter = activeTab === 'applications' ? selectedAppTemplate : selectedImplTemplate;
      const templateParam = templateFilter !== 'all' ? `&template_id=${templateFilter}` : '';
      
      const response = await axios.get(`${API}${endpoint}?user_id=${user.user_id}${templateParam}`);
      
      setPreviewData(response.data);
      
      // Select all by default
      const allSelected = {};
      response.data.preview_items.forEach(item => {
        allSelected[item.email_id] = true;
      });
      setSelectedEmails(allSelected);
      
      if (response.data.total_emails === 0) {
        toast.info('Δεν βρέθηκαν εκκρεμή δικαιολογητικά για αποστολή');
      }
      
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Σφάλμα κατά τη σύνταξη των emails');
    } finally {
      setGenerating(false);
    }
  };

  const toggleEmailSelection = (emailId) => {
    setSelectedEmails(prev => ({
      ...prev,
      [emailId]: !prev[emailId]
    }));
  };

  const toggleSelectAll = () => {
    if (!previewData) return;
    
    const allSelected = Object.values(selectedEmails).every(v => v);
    const newSelection = {};
    previewData.preview_items.forEach(item => {
      newSelection[item.email_id] = !allSelected;
    });
    setSelectedEmails(newSelection);
  };

  const togglePreviewExpand = (emailId) => {
    setExpandedPreviews(prev => ({
      ...prev,
      [emailId]: !prev[emailId]
    }));
  };

  const removeFromPreview = (emailId) => {
    setPreviewData(prev => ({
      ...prev,
      preview_items: prev.preview_items.filter(item => item.email_id !== emailId),
      total_emails: prev.total_emails - 1
    }));
    setSelectedEmails(prev => {
      const newSelected = { ...prev };
      delete newSelected[emailId];
      return newSelected;
    });
  };

  const sendNotifications = async () => {
    const selectedItems = previewData.preview_items.filter(
      item => selectedEmails[item.email_id]
    );
    
    if (selectedItems.length === 0) {
      toast.warning('Επιλέξτε τουλάχιστον ένα email για αποστολή');
      return;
    }
    
    setSending(true);
    
    try {
      // Use edited HTML if available, then inject signature
      const signatureHtml = buildSignatureHtml();
      const enrichedItems = selectedItems.map(item => {
        let html = editedHtmls[item.email_id] || item.html_preview;
        if (signatureHtml) {
          const lastDiv = html.lastIndexOf('</div>');
          if (lastDiv > 0) {
            html = html.substring(0, lastDiv) + signatureHtml + html.substring(lastDiv);
          } else {
            html += signatureHtml;
          }
        }
        return { ...item, html_preview: html };
      });
      
      const response = await axios.post(
        `${API}/notifications/send-with-content?user_id=${user.user_id}`,
        enrichedItems
      );
      
      // Show scheduled times
      const firstSend = formatDateTime(response.data.first_send_at);
      const lastSend = formatDateTime(response.data.last_send_at);
      
      setBatchStatus({
        batch_id: response.data.batch_id,
        total: selectedItems.length,
        queued: response.data.queued,
        failed: response.data.failed,
        first_send_at: response.data.first_send_at,
        last_send_at: response.data.last_send_at,
        status: 'processing'
      });
      
      if (response.data.queued > 1) {
        toast.success(`${response.data.queued} emails θα σταλούν από ${firstSend} έως ${lastSend}`);
      } else {
        toast.success(`Το email θα σταλεί στις ${firstSend}`);
      }
      
      // Refresh pending emails
      fetchPendingEmails();
      
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast.error('Σφάλμα κατά την αποστολή');
    } finally {
      setSending(false);
    }
  };

  const pollBatchStatus = async (batchId) => {
    try {
      const response = await axios.get(`${API}/notifications/batch/${batchId}/status`);
      setBatchStatus(response.data);
      
      if (response.data.status !== 'completed' && response.data.pending > 0) {
        setTimeout(() => pollBatchStatus(batchId), 3000);
      } else {
        fetchPendingEmails();
      }
    } catch (error) {
      console.error('Error polling batch status:', error);
    }
  };

  const selectedCount = Object.values(selectedEmails).filter(v => v).length;

  const formatDateTime = (isoString) => {
    if (!isoString) return 'Άμεσα';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return 'Άμεσα';
      return date.toLocaleString('el-GR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Άμεσα';
    }
  };

  // Get scheduled time (30 seconds from now for display purposes)
  const getScheduledTime = () => {
    const now = new Date();
    return now.toLocaleString('el-GR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div 
      className="min-h-screen bg-slate-900 relative"
      style={{
        backgroundImage: `url(${BG_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-slate-900/85 backdrop-blur-sm"></div>
      
      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <div className="bg-black/30 backdrop-blur-md border-b border-white/10 sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/select-module')}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="back-btn"
                >
                  <ArrowLeft className="h-5 w-5 text-white" />
                </button>
                <div>
                  <h1 className="text-xl font-bold text-white flex items-center gap-2">
                    <Mail className="h-6 w-6 text-cyan-400" />
                    Βοηθός Ειδοποιήσεων Συμβούλου
                  </h1>
                  <p className="text-sm text-white/60">Χειροκίνητη αποστολή υπενθυμίσεων</p>
                </div>
              </div>
              
              {/* Signature + Tab Switcher */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { fetchSignature(); setSignatureDialogOpen(true); }}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors text-sm"
                  title="Ρυθμίσεις Υπογραφής"
                  data-testid="signature-settings-btn"
                >
                  <Settings className="h-4 w-4" />
                  Υπογραφή
                </button>
              </div>
              <div className="flex bg-black/30 rounded-lg p-1">
                <button
                  onClick={() => { setActiveTab('applications'); setPreviewData(null); setBatchStatus(null); }}
                  data-testid="applications-tab"
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeTab === 'applications' 
                      ? 'bg-emerald-500 text-white' 
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  <FileText className="h-4 w-4" />
                  Βοηθός Αιτήσεων
                </button>
                <button
                  onClick={() => { setActiveTab('implementations'); setPreviewData(null); setBatchStatus(null); }}
                  data-testid="implementations-tab"
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeTab === 'implementations' 
                      ? 'bg-blue-500 text-white' 
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  <Building className="h-4 w-4" />
                  Βοηθός Υλοποιήσεων
                </button>
                <button
                  onClick={() => { setActiveTab('reports'); setPreviewData(null); setBatchStatus(null); }}
                  data-testid="reports-tab"
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                    activeTab === 'reports' 
                      ? 'bg-amber-500 text-white' 
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  <Clock className="h-4 w-4" />
                  Αναφορές Αποστολών
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Reports Tab */}
          {activeTab === 'reports' && (
            <NotificationReportPanel user={user} />
          )}

          {/* Pending Emails Section - Show only when not in preview and we have pending */}
          {activeTab !== 'reports' && pendingEmails.length > 0 && !generating && !previewData && !batchStatus && (
            <div className="mb-8 bg-amber-500/10 backdrop-blur-sm rounded-xl border border-amber-500/30 p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-amber-400" />
                  <h3 className="text-lg font-semibold text-white">
                    Emails στην Ουρά Αποστολής ({pendingEmails.length})
                  </h3>
                  <span className="text-xs text-amber-400/70 bg-amber-500/20 px-2 py-1 rounded">
                    Όλα τα modules
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAllPendingEmails}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Διαγραφή Όλων
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-auto">
                {pendingEmails.map((email, idx) => (
                  <div key={email.email_id || idx} className="flex items-center justify-between bg-black/20 rounded-lg px-4 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-medium truncate">{email.recipient}</div>
                      <div className="text-white/60 text-xs truncate">{email.subject}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-amber-400 text-xs whitespace-nowrap">
                        Αποστολή: {formatDateTime(email.scheduled_for)}
                      </div>
                      <button
                        onClick={() => deleteSinglePendingEmail(email.email_id)}
                        className="p-1 hover:bg-red-500/20 rounded text-red-400"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Initial State */}
          {activeTab !== 'reports' && !previewData && !generating && !batchStatus && (
            <div className="text-center py-16">
              <div className="mb-8">
                <div className="inline-block p-6 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl backdrop-blur-sm border border-cyan-500/30">
                  <Mail className="h-16 w-16 text-cyan-400 mx-auto" />
                </div>
              </div>
              
              <h2 className="text-3xl font-bold text-white mb-4">
                {activeTab === 'applications' ? 'Βοηθός Αιτήσεων' : 'Βοηθός Υλοποιήσεων'}
              </h2>
              <p className="text-white/70 text-lg mb-6 max-w-xl mx-auto">
                Θα συντάξω αυτόματα τα emails υπενθύμισης για τα εκκρεμή δικαιολογητικά 
                των {activeTab === 'applications' ? 'αιτήσεων' : 'υλοποιήσεων'} σας. 
                Μπορείτε να τα ελέγξετε πριν την αποστολή.
              </p>
              
              {/* Template Filter */}
              <div className="mb-8 flex items-center justify-center gap-3">
                <span className="text-white/70">Φιλτράρισμα κατά πρότυπο:</span>
                <select
                  value={activeTab === 'applications' ? selectedAppTemplate : selectedImplTemplate}
                  onChange={(e) => activeTab === 'applications' 
                    ? setSelectedAppTemplate(e.target.value) 
                    : setSelectedImplTemplate(e.target.value)
                  }
                  className="px-4 py-2 rounded-lg bg-white/10 border border-white/30 text-white 
                             focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="all" className="text-slate-900">Όλα τα πρότυπα</option>
                  {(activeTab === 'applications' ? appTemplates : implTemplates).map(t => (
                    <option key={t.template_id} value={t.template_id} className="text-slate-900">
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <Button
                onClick={generatePreview}
                size="lg"
                className={`px-8 py-6 text-lg font-semibold ${
                  activeTab === 'applications' 
                    ? 'bg-emerald-500 hover:bg-emerald-600' 
                    : 'bg-blue-500 hover:bg-blue-600'
                }`}
                data-testid="generate-btn"
              >
                <RefreshCw className="h-5 w-5 mr-2" />
                Αυτόματη Σύνταξη Email
              </Button>
            </div>
          )}

          {/* Generating State with Typewriter Effect */}
          {generating && (
            <div className="text-center py-16">
              <div className="mb-8">
                <div className="inline-block p-6 bg-gradient-to-br from-cyan-500/30 to-blue-500/30 rounded-2xl backdrop-blur-sm border border-cyan-500/50 animate-pulse">
                  <Loader2 className="h-16 w-16 text-cyan-400 mx-auto animate-spin" />
                </div>
              </div>
              
              <div className="h-20">
                <TypewriterText 
                  key={generatingStep}
                  text={generatingMessages[generatingStep]}
                  speed={40}
                  className="text-2xl font-bold text-white"
                />
              </div>
              
              <p className="text-white/50 text-sm mt-4">Παρακαλώ περιμένετε</p>
              
              {/* Progress dots */}
              <div className="flex items-center justify-center gap-2 mt-6">
                {generatingMessages.map((_, idx) => (
                  <div 
                    key={idx}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      idx <= generatingStep ? 'bg-cyan-400' : 'bg-white/20'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Preview State */}
          {previewData && !batchStatus && (
            <div className="space-y-6">
              {/* Tab indicator */}
              <div className={`text-center py-2 rounded-lg ${
                activeTab === 'applications' 
                  ? 'bg-emerald-500/20 border border-emerald-500/30' 
                  : 'bg-blue-500/20 border border-blue-500/30'
              }`}>
                <span className={`font-semibold ${
                  activeTab === 'applications' ? 'text-emerald-400' : 'text-blue-400'
                }`}>
                  {activeTab === 'applications' ? '📋 Preview Αιτήσεων' : '🏗️ Preview Υλοποιήσεων'}
                </span>
              </div>

              {/* Stats Bar */}
              <div className="bg-black/30 backdrop-blur-sm rounded-xl p-4 flex items-center justify-between border border-white/10">
                <div className="flex items-center gap-6">
                  <div className="text-white">
                    <span className="text-3xl font-bold text-cyan-400">{previewData.total_emails}</span>
                    <span className="text-white/70 ml-2">emails</span>
                  </div>
                  <div className="text-white">
                    <span className="text-3xl font-bold text-purple-400">{previewData.total_projects}</span>
                    <span className="text-white/70 ml-2">έργα</span>
                  </div>
                  <div className="text-white">
                    <span className="text-3xl font-bold text-emerald-400">{selectedCount}</span>
                    <span className="text-white/70 ml-2">επιλεγμένα</span>
                  </div>
                  <div className="text-white/60 text-sm">
                    <Clock className="h-4 w-4 inline mr-1" />
                    Αποστολή: {getScheduledTime()}
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={toggleSelectAll}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    {Object.values(selectedEmails).every(v => v) ? 'Αποεπιλογή Όλων' : 'Επιλογή Όλων'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={generatePreview}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Ανανέωση
                  </Button>
                  <Button
                    onClick={sendNotifications}
                    disabled={selectedCount === 0 || sending}
                    className={`${
                      activeTab === 'applications' 
                        ? 'bg-emerald-500 hover:bg-emerald-600' 
                        : 'bg-blue-500 hover:bg-blue-600'
                    }`}
                    data-testid="send-btn"
                  >
                    {sending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Αποστολή ({selectedCount})
                  </Button>
                </div>
              </div>

              {/* Email List */}
              {previewData.total_emails === 0 ? (
                <div className="bg-black/30 backdrop-blur-sm rounded-xl p-12 text-center border border-white/10">
                  <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Όλα εντάξει!</h3>
                  <p className="text-white/70">
                    Δεν υπάρχουν εκκρεμή δικαιολογητικά για υπενθύμιση στις {activeTab === 'applications' ? 'αιτήσεις' : 'υλοποιήσεις'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {previewData.preview_items.map((item) => (
                    <div 
                      key={item.email_id}
                      className={`bg-black/30 backdrop-blur-sm rounded-xl overflow-hidden transition-all border ${
                        selectedEmails[item.email_id] ? 'border-cyan-500/50 ring-1 ring-cyan-500/30' : 'border-white/10'
                      }`}
                    >
                      {/* Email Header */}
                      <div className="p-4 flex items-start gap-3">
                        <Checkbox
                          checked={selectedEmails[item.email_id] || false}
                          onCheckedChange={() => toggleEmailSelection(item.email_id)}
                          className="border-white/40 data-[state=checked]:bg-cyan-500 mt-1 shrink-0"
                        />
                        
                        <div className="flex-1 min-w-0">
                          {/* Recipient name & email - always visible */}
                          <div className="mb-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              {item.recipient_name ? (
                                <span className="text-white font-semibold text-sm" data-testid="recipient-name">{item.recipient_name}</span>
                              ) : null}
                              <span className="text-cyan-300 text-sm font-medium" data-testid="recipient-email">{item.recipient_email}</span>
                              <span className="px-2 py-0.5 bg-white/20 rounded text-xs text-white/70 shrink-0">
                                {item.recipient_role}
                              </span>
                            </div>
                          </div>
                          {/* Project & client info */}
                          <div className="text-sm text-white/60 truncate">
                            {item.project_title} • {item.client_name}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm shrink-0">
                          {item.pending_count > 0 && (
                            <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded">
                              {item.pending_count} εκκρεμή
                            </span>
                          )}
                          {item.rejected_count > 0 && (
                            <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded">
                              {item.rejected_count} απορριφθέντα
                            </span>
                          )}
                        </div>
                        
                        <button
                          onClick={() => editingEmailId === item.email_id ? saveEditEmail(item.email_id) : openEditEmail(item)}
                          className={`p-2 rounded-lg transition-colors ${
                            editingEmailId === item.email_id 
                              ? 'bg-emerald-500/20 text-emerald-400' 
                              : editedHtmls[item.email_id] 
                                ? 'bg-cyan-500/20 text-cyan-400' 
                                : 'hover:bg-white/10 text-white/50'
                          }`}
                          title={editingEmailId === item.email_id ? 'Αποθήκευση αλλαγών' : 'Επεξεργασία email'}
                          data-testid={`edit-email-${item.email_id}`}
                        >
                          {editingEmailId === item.email_id ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Pencil className="h-4 w-4" />
                          )}
                        </button>
                        
                        {editingEmailId === item.email_id && (
                          <button
                            onClick={() => cancelEditEmail(item.email_id)}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/50"
                            title="Ακύρωση"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                        
                        <button
                          onClick={() => togglePreviewExpand(item.email_id)}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                          title="Προβολή email"
                        >
                          {expandedPreviews[item.email_id] ? (
                            <ChevronDown className="h-5 w-5 text-white/70" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-white/70" />
                          )}
                        </button>
                        
                        <button
                          onClick={() => removeFromPreview(item.email_id)}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-red-400"
                          title="Αφαίρεση"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {/* Edited indicator */}
                      {editedHtmls[item.email_id] && editingEmailId !== item.email_id && (
                        <div className="mx-4 mt-1 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded text-xs text-cyan-300">
                          <strong>Επεξεργασμένο</strong> - Το κείμενο του email έχει τροποποιηθεί
                        </div>
                      )}
                      
                      {/* Email Preview */}
                      {expandedPreviews[item.email_id] && (
                        <div className="border-t border-white/10 p-4 bg-black/20">
                          <div className="text-xs text-white/50 mb-2">
                            <strong>Θέμα:</strong> {item.subject}
                          </div>
                          {editingEmailId === item.email_id && (
                            <div className="mb-2 flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 px-3 py-1.5 rounded">
                              <Pencil className="h-3 w-3" />
                              Κάντε κλικ στο κείμενο για να το επεξεργαστείτε απευθείας
                            </div>
                          )}
                          <div 
                            id={`email-preview-${item.email_id}`}
                            className={`bg-white rounded-lg p-4 text-sm max-h-96 overflow-auto ${
                              editingEmailId === item.email_id ? 'ring-2 ring-cyan-500 outline-none cursor-text' : ''
                            }`}
                            contentEditable={editingEmailId === item.email_id}
                            suppressContentEditableWarning
                            dangerouslySetInnerHTML={{ __html: editedHtmls[item.email_id] || item.html_preview }}
                          />
                          {(signatureText || signatureHasImage) && (
                            <div className="mt-2 bg-white rounded-lg p-4 border-t-2 border-slate-200">
                              <div className="text-xs text-slate-400 mb-1">Υπογραφή:</div>
                              {signatureText && <p className="text-sm text-slate-600 whitespace-pre-line">{signatureText}</p>}
                              {signatureHasImage && signaturePreviewSrc && (
                                <img src={signaturePreviewSrc} alt="Υπογραφή" className="max-w-[200px] max-h-[80px] mt-1" />
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Batch Status / Results */}
          {batchStatus && (
            <div className="space-y-6">
              <div className="bg-black/30 backdrop-blur-sm rounded-xl p-8 text-center border border-white/10">
                {batchStatus.status === 'processing' ? (
                  <>
                    <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-white mb-4">
                      Τα emails προστέθηκαν στην ουρά!
                    </h3>
                    <div className="bg-black/20 rounded-lg p-4 inline-block">
                      <div className="text-white/70 text-sm mb-1">Χρονοδιάγραμμα αποστολής:</div>
                      <div className="text-cyan-400 font-semibold">
                        {formatDateTime(batchStatus.first_send_at)} - {formatDateTime(batchStatus.last_send_at)}
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-6 mt-6">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-emerald-400">{batchStatus.queued}</div>
                        <div className="text-white/60 text-sm">Emails στην ουρά</div>
                      </div>
                    </div>
                    <p className="text-white/50 text-sm mt-4">
                      Τα emails θα αποσταλούν με διαστήματα 45-90 δευτερολέπτων για αποφυγή spam
                    </p>
                    <Button
                      onClick={() => { setPreviewData(null); setBatchStatus(null); fetchPendingEmails(); }}
                      className="mt-6"
                      variant="outline"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Νέα Αποστολή
                    </Button>
                  </>
                ) : (
                  <>
                    {batchStatus.failed === 0 ? (
                      <CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto mb-4" />
                    ) : (
                      <AlertCircle className="h-16 w-16 text-amber-400 mx-auto mb-4" />
                    )}
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {batchStatus.failed === 0 ? 'Όλα τα emails απεστάλησαν!' : 'Η αποστολή ολοκληρώθηκε'}
                    </h3>
                    <div className="flex items-center justify-center gap-6 mt-4">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-emerald-400">{batchStatus.queued}</div>
                        <div className="text-white/60 text-sm">Επιτυχία</div>
                      </div>
                      {batchStatus.failed > 0 && (
                        <div className="text-center">
                          <div className="text-3xl font-bold text-red-400">{batchStatus.failed}</div>
                          <div className="text-white/60 text-sm">Αποτυχία</div>
                        </div>
                      )}
                    </div>
                    
                    {batchStatus.failed_emails?.length > 0 && (
                      <div className="mt-6 bg-red-500/10 rounded-lg p-4 text-left border border-red-500/30">
                        <h4 className="text-red-400 font-medium mb-2">Αποτυχημένες αποστολές:</h4>
                        <ul className="text-white/70 text-sm space-y-1">
                          {batchStatus.failed_emails.map((fe, idx) => (
                            <li key={idx}>{fe.email}: {fe.reason}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <Button
                      onClick={() => { setPreviewData(null); setBatchStatus(null); fetchPendingEmails(); }}
                      className="mt-6"
                      variant="outline"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Νέα Αποστολή
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Signature Settings Dialog */}
      <Dialog open={signatureDialogOpen} onOpenChange={setSignatureDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Ρυθμίσεις Υπογραφής Email
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Κείμενο Υπογραφής</Label>
              <Textarea
                value={signatureText}
                onChange={(e) => setSignatureText(e.target.value)}
                placeholder="π.χ. Με εκτίμηση,&#10;Αλέξανδρος Καλεντζής&#10;ESPA Manager&#10;Τηλ: 210 1234567"
                rows={5}
                className="resize-none"
                data-testid="signature-text"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Εικόνα Υπογραφής
              </Label>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  id="signature-image-upload"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    // Validate it's a real image on the client side first
                    if (!file.type.startsWith('image/')) {
                      toast.error('Παρακαλώ επιλέξτε αρχείο εικόνας (PNG, JPG)');
                      e.target.value = '';
                      return;
                    }
                    try {
                      // Upload to server (server validates & compresses)
                      const formData = new FormData();
                      formData.append('file', file);
                      const res = await axios.post(`${API}/notifications/signature/upload-image?user_id=${user.user_id}`, formData);
                      // Use the compressed base64 from server response for preview
                      if (res.data.image_url) {
                        setSignaturePreviewSrc(res.data.image_url);
                      }
                      setSignatureHasImage(true);
                      toast.success('Η εικόνα ανέβηκε επιτυχώς');
                    } catch (error) {
                      const msg = error.response?.data?.detail || 'Σφάλμα ανεβάσματος εικόνας';
                      toast.error(msg);
                      setSignatureHasImage(false);
                      setSignaturePreviewSrc('');
                    }
                    e.target.value = '';
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('signature-image-upload').click()}
                  className="flex items-center gap-2"
                  data-testid="signature-upload-btn"
                >
                  <ImageIcon className="h-4 w-4" />
                  {signatureHasImage ? 'Αλλαγή εικόνας' : 'Ανέβασμα εικόνας'}
                </Button>
                {signatureHasImage && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-rose-500"
                    onClick={() => { setSignaturePreviewSrc(''); setSignatureHasImage(false); }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {signaturePreviewSrc && (
                <div className="p-3 bg-slate-50 rounded-lg border">
                  <p className="text-xs text-slate-500 mb-2">Προεπισκόπηση:</p>
                  <img 
                    src={signaturePreviewSrc} 
                    alt="Υπογραφή" 
                    className="max-w-[250px] max-h-[100px]"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>
            <Button onClick={saveSignature} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="save-signature">
              Αποθήκευση Υπογραφής
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationAssistant;
